import * as React from 'react'
import { animate, type AnimationOptions } from 'motion/react'

import { getElementSizeInEm, targetWidths } from '../utils/getElementSize'
import type { EmUnit } from '../types'

type DigitState = {
  width: EmUnit | undefined
  fontSize: EmUnit | undefined
}

/**
 * Custom hook to manage digit state and width calculations
 * Handles animation and width measurements
 */
export const useAnimateDigit = (
  animateRef: React.RefObject<HTMLSpanElement>,
  widthRef: React.RefObject<HTMLSpanElement>,
  value: number,
  initialValue: number,
  transition: AnimationOptions,
  isFirstRender: boolean,
): DigitState => {
  const [width, setWidth] = React.useState<EmUnit>()
  const [fontSize, setFontSize] = React.useState<EmUnit>()
  const prevValue = React.useRef(initialValue)

  /**
   * FLIP Animation Technique (First, Last, Invert, Play)
   *
   * This animation uses useLayoutEffect to ensure smooth transitions between numbers:
   * 1. First: We measure the initial position of the digit
   * 2. Last: We know the final position (y: 0)
   * 3. Invert: We calculate the transform needed to make the digit appear to start from its old position
   * 4. Play: We animate to the final position
   *
   * We use useLayoutEffect instead of useEffect because:
   * - It runs synchronously after DOM mutations but before browser paint
   * - This prevents any visual flicker or jump in the animation
   * - Ensures measurements and animation setup happen before the user sees any changes
   */
  React.useLayoutEffect(() => {
    if (!animateRef.current || value === prevValue.current) return

    // Handle animation
    const box = animateRef.current.getBoundingClientRect()
    const refBox = widthRef.current?.getBoundingClientRect()

    const deltaTop = box.top - (refBox?.top ?? box.top)
    const initialY = box.height * (value - prevValue.current) + deltaTop

    // ✨ animate digit ✨
    animate(animateRef.current, { y: [initialY, 0] }, transition)

    // Handle width
    if (widthRef.current) {
      const { width } = getElementSizeInEm(widthRef.current)
      targetWidths.set(widthRef.current, width)
      setWidth(width)
    }

    return () => {
      prevValue.current = value
    }
  }, [value, transition])

  // Handle initial width on first render
  React.useEffect(() => {
    if (isFirstRender && initialValue === value) return
    if (!widthRef.current) return

    const { width, fontSize } = getElementSizeInEm(widthRef.current)
    if (widthRef.current) targetWidths.set(widthRef.current, width)
    setWidth(width)
    setFontSize(fontSize)
  }, [value])

  return { width, fontSize }
}
